import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import Core from '../../class/Core';
import { getAllowedUrls, hasPermission } from '../../utils/PermissionUtils';

/**
 * PrivateRoute - Componente que protege rotas com autenticação + permissões
 *
 * Fluxo:
 * 1. Verifica se usuário está autenticado (token válido e não expirado)
 * 2. Se não autenticado → Redirect para "/"
 * 3. Se autenticado, verifica se tem permissão na rota
 * 4. Se sem permissão → Redirect para "/manager/403"
 * 5. Se com permissão → Renderiza o componente
 *
 * @param {boolean} skipPermissionCheck - Se true, pula validação de permissões
 *                                         (mantém autenticação obrigatória)
 *                                         Usar para rotas públicas a usuários autenticados
 *                                         (ex: dashboard, perfil)
 *
 * @example
 * // Rota com validação completa
 * <PrivateRoute exact path="/manager/dashboard" component={Dashboard} />
 *
 * @example
 * // Rota que pula validação de permissões
 * <PrivateRoute exact path="/manager/perfil" component={Perfil} skipPermissionCheck={true} />
 */
const PrivateRoute = ({ component: Component, user, skipPermissionCheck = false, ...rest }) => {
  const core = new Core();

  return (
    <Route
      {...rest}
      render={(props) => {
        // ETAPA 1: Verificar autenticação
        // Usar getAuth() em vez de isAuth() para não causar redirecionamento automático
        const userAuth = core.getAuth();

        if (!userAuth) {
          // Não autenticado → redirect para login
          return <Redirect to="/" />;
        }

        // Validar expiração do token
        const now = new Date().getTime();
        const expiresAt = userAuth.expires_in * 1000;

        if (now >= expiresAt) {
          // Token expirado → redirect para login
          core.removeStorage('manager_log');
          return <Redirect to="/" />;
        }

        // ETAPA 2: Verificar permissões
        // Usar userAuth (localStorage) para consistência com verificação de autenticação
        const allowedUrls = userAuth.allowedUrls || getAllowedUrls(userAuth);
        const currentPath = props.location.pathname;

        // Skip permission check se prop ativada (para rotas públicas a usuários autenticados)
        if (skipPermissionCheck) {
          return <Component {...props} />;
        }

        if (!hasPermission(currentPath, allowedUrls)) {
          // Autenticado mas sem permissão → redirect 403
          return <Redirect to="/manager/403" />;
        }

        // ETAPA 3: Renderizar componente
        return <Component {...props} />;
      }}
    />
  );
};

function mapStateToProps(state) {
  return {
    user: state.user
  };
}

export default connect(mapStateToProps)(PrivateRoute);
